import axios from "axios";

/**
 * AdsApiService - ad content and engagement reporting.
 *
 * Ad content now lives in MySQL rather than Firestore, so the display path
 * fetches through here. The superadmin methods back the management console and
 * the advertiser report.
 */
class AdsApiService {
  /**
   * Ads a client should display, plus the cache version token.
   *
   * @param {string} clinicURL
   * @param {string} branchURL
   * @param {"carousel"|"popup"} [placement]
   * @returns {Promise} { version, ads }
   */
  static async getAds(clinicURL, branchURL, placement) {
    const query = placement ? `?placement=${encodeURIComponent(placement)}` : "";
    return axios.get(`/api/ads/${clinicURL}/${branchURL}${query}`);
  }

  // SUPERADMIN CONSOLE

  /** @returns {Promise} { success, data: { ads, popupPaused, popupPausedAt } } */
  static async listAds() {
    return axios.get("/api/superadmin/ads");
  }

  /**
   * Take the ad popup down system-wide, or put it back. The carousel is not
   * affected.
   *
   * Each ad keeps its own placement flags, so resuming pops up exactly what was
   * popping up before rather than promoting carousel-only ads into the popup.
   *
   * @param {boolean} paused
   * @returns {Promise} { success, data: { paused, changedAt } }
   */
  static async setPopupPaused(paused) {
    return axios.put("/api/superadmin/ads/popup-pause", { paused });
  }

  /** @param {Object} ad @returns {Promise} */
  static async createAd(ad) {
    return axios.post("/api/superadmin/ads", ad);
  }

  /** @param {number} adId @param {Object} ad @returns {Promise} */
  static async updateAd(adId, ad) {
    return axios.put(`/api/superadmin/ads/${adId}`, ad);
  }

  /**
   * A presigned S3 PUT for one ad image, plus the CloudFront URL to store as
   * the ad's imageUrl once the upload succeeds.
   *
   * @param {Object} file
   * @param {string} file.contentType the file's MIME type
   * @param {number} file.size the file's size in bytes
   * @returns {Promise} { success, data: { uploadUrl, publicUrl, key } }
   */
  static async createImageUpload({ contentType, size }) {
    return axios.post("/api/superadmin/ads/image-upload", { contentType, size });
  }

  /** @param {number} adId @returns {Promise} */
  static async deleteAd(adId) {
    return axios.delete(`/api/superadmin/ads/${adId}`);
  }

  /**
   * Advertiser engagement report.
   *
   * @param {Object} params
   * @param {string} params.from YYYY-MM-DD
   * @param {string} params.to YYYY-MM-DD (at most 92 days after `from`)
   * @param {string[]} [params.groupBy] any of day, placement, branch, role
   * @returns {Promise} { success, data: { range, totals, rows } }
   */
  static async getAdReport({ from, to, groupBy = [] }) {
    return axios.get("/api/superadmin/ads/report", {
      params: {
        from,
        to,
        ...(groupBy.length ? { groupBy: groupBy.join(",") } : {}),
      },
    });
  }
}

export default AdsApiService;
